import { useEffect, useState, type ReactNode } from "react";
import { useT } from "../../lib/i18n";
import { Thumb } from "../ui/Thumb";

/**
 * 玩家身份：头像 + 昵称 + 可读 id。玩家面板与客户端数据页共用——
 * 两页各画一份时字号、占位圆、id 截断口径已经漂过一次（leo 2026-08-26）。
 *
 * size="sm" 是表格单元格（24px 头像、两行），size="lg" 是详情头（56px 头像，
 * 昵称旁可挂标签，id 下面可塞附加行）。
 */

/**
 * user_id 是 VARBINARY，多数是可打印文本（`11080913`、`qa-cards-…`、`A0:12345` 这种复合键），
 * 直接显示 hex 既看不懂又长到 40+ 字符，把玩家列撑爆。能解成可打印文本就显示文本，
 * 解不出来（真二进制 id）才退回 hex。
 */
export function readableId(hex: string): string {
  if (!/^[0-9a-f]*$/i.test(hex) || hex.length === 0 || hex.length % 2 !== 0) return hex;
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  // 出现非 ASCII 可打印字节就当二进制，不硬解成乱码
  for (const b of bytes) if (b < 0x20 || b > 0x7e) return hex;
  return new TextDecoder().decode(bytes);
}

/**
 * 玩家在游戏里显示的名字：自设昵称 > 建号快照（与服务端 main-profile.ts 同口径）。
 * 管端只显示快照名的话，运营看到的名字和玩家、客户端看到的对不上。
 */
export function shownName(p: { nickname: string | null; customNickname?: string | null } | null | undefined): string | null {
  return p?.customNickname || p?.nickname || null;
}

/** 图挂了退回占位圆（不是隐藏）——隐藏会让昵称左边空一块，像漏渲染 */
function Avatar({ url, cls }: { url: string | null | undefined; cls: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [url]);
  if (!url || failed) return <div className={`${cls} shrink-0 rounded-full bg-fill`} />;
  return <Thumb src={url} className={`${cls} shrink-0 rounded-full bg-fill object-cover`} onError={() => setFailed(true)} />;
}

export function PlayerIdent({ nickname, userIdHex, avatar, size = "sm", badge, children }: {
  nickname: string | null | undefined;
  userIdHex: string;
  avatar?: string | null;
  size?: "sm" | "lg";
  /** 昵称旁的标记（付费 ◆ / 标签） */
  badge?: ReactNode;
  /** 仅 lg：id 行下面的附加内容 */
  children?: ReactNode;
}) {
  const t = useT();
  const name = nickname ?? <span className="text-ink-placeholder">{t("pp.noNickname")}</span>;
  if (size === "lg") {
    return (
      <div className="flex items-start gap-3">
        <Avatar url={avatar} cls="h-14 w-14" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-base font-semibold text-ink">{name}{badge}</div>
          {/* title 给全量原值：截断后要能靠悬停看全，也方便复制 */}
          <div className="mt-1 text-[11px] text-ink-placeholder" title={userIdHex}>userId {readableId(userIdHex)}</div>
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Avatar url={avatar} cls="h-6 w-6" />
      <div className="min-w-0">
        <div className="truncate">{name}{badge}</div>
        <div className="truncate text-[11px] text-ink-placeholder" title={userIdHex}>{readableId(userIdHex)}</div>
      </div>
    </div>
  );
}
